import { useState, useEffect } from "react";
import { FullScreenModal } from "@/components/FullScreenModal";
import { KeyboardShortcutsHint } from "@/components/KeyboardShortcutsHint";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { DocumentEditor } from "./Editor/Editor";
import { motion, AnimatePresence } from "framer-motion";
import type { Document } from "document-api-client";

interface DocumentModalBrowserProps {
    documents: Document[];
    open: boolean;
    onClose: () => void;
    onApproved?: (documentId: string) => void;
    onSaved?: (documentId: string) => void;
    title?: string;
    initialIndex?: number;
}

export function DocumentModalBrowser({
    documents,
    open,
    onClose,
    onApproved,
    onSaved,
    title = "Document Browser",
    initialIndex = 0,
}: DocumentModalBrowserProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [remainingDocuments, setRemainingDocuments] = useState<Document[]>(
        [],
    );
    const [direction, setDirection] = useState<"left" | "right">("right");

    // Initialize with provided receipts - only on open
    useEffect(() => {
        if (open) {
            console.log("Initializing documents and index", {
                receipts: documents,
                initialIndex,
            });
            setRemainingDocuments(documents);
            setCurrentIndex(initialIndex);
        }
    }, [open]); // Only re-run when modal opens

    // Debug effect to track current index changes
    useEffect(() => {
        console.log(`Current index changed to: ${currentIndex}`);
        console.log(
            `Current document: ${currentDocument?.documentId}, isApproved: ${currentDocument?.isApproved}`,
        );
    }, [currentIndex, remainingDocuments]);

    // Handle navigation
    const handleNavigate = (dir: "next" | "prev") => {
        if (dir === "next" && currentIndex < remainingDocuments.length - 1) {
            setDirection("right");
            setCurrentIndex(currentIndex + 1);
        } else if (dir === "prev" && currentIndex > 0) {
            setDirection("left");
            setCurrentIndex(currentIndex - 1);
        }
    };

    //TODO: use with keyboard hints to show available shortcuts
    // Keyboard shortcuts
    const shortcuts = useKeyboardShortcuts([
        {
            key: "ArrowLeft",
            description: "Previous",
            action: () => handleNavigate("prev"),
            enabled: open && currentIndex > 0,
        },
        {
            key: "ArrowRight",
            description: "Next",
            action: () => handleNavigate("next"),
            enabled: open && currentIndex < remainingDocuments.length - 1,
        },
        {
            key: "Escape",
            description: "Close",
            action: onClose,
            enabled: open,
        },
    ]);

    // Handle approval
    const handleApprove = async (documentId: string) => {
        if (!onApproved) return;

        setIsLoading(true);
        try {
            // Find the current document
            const currentDocument = remainingDocuments.find(
                (r) => r.documentId === documentId,
            );
            if (!currentDocument) {
                throw new Error("Document not found");
            }

            // First update our local state
            const updatedDocuments = remainingDocuments.map((r) =>
                r.documentId === documentId ? { ...r, isApproved: true } : r,
            );

            // Find the next unapproved document (first check after current index)
            const nextUnapprovedIndex = updatedDocuments.findIndex(
                (r, idx) => idx > currentIndex && !r.isApproved,
            );

            // If no unapproved document after current, check from beginning
            const prevUnapprovedIndex =
                nextUnapprovedIndex === -1
                    ? updatedDocuments.findIndex((r) => !r.isApproved)
                    : -1;

            // Update documents state
            setRemainingDocuments(updatedDocuments);

            // Navigate to next unapproved document or close if none left
            if (nextUnapprovedIndex !== -1) {
                // Navigate to next unapproved document
                console.log(
                    `Navigating to next unapproved document at index ${nextUnapprovedIndex}`,
                );
                setDirection("right");
                setCurrentIndex(nextUnapprovedIndex);
            } else if (prevUnapprovedIndex !== -1) {
                // Navigate to previous unapproved document
                console.log(
                    `Navigating to previous unapproved document at index ${prevUnapprovedIndex}`,
                );
                setDirection("left");
                setCurrentIndex(prevUnapprovedIndex);
            } else {
                // No more unapproved documents, close the overlay
                console.log("No more unapproved documents, closing overlay");
                onClose();
            }

            // Notify parent component AFTER we've updated our local state and navigated
            await onApproved(documentId);
        } catch (error) {
            console.error("Error approving document:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle revoke approval (without navigation animation)
    const handleRevokeApprove = async (documentId: string) => {
        if (!onApproved) return;

        setIsLoading(true);
        try {
            // Find the current document
            const currentDocument = remainingDocuments.find(
                (r) => r.documentId === documentId,
            );
            if (!currentDocument) {
                throw new Error("Document not found");
            }

            // Notify parent component that approval was revoked
            await onApproved(documentId);

            // Update the document in our local state (no navigation)
            setRemainingDocuments((prev) =>
                prev.map((r) =>
                    r.documentId === documentId
                        ? { ...r, isApproved: false }
                        : r,
                ),
            );
        } catch (error) {
            console.error("Error revoking approval:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle save
    const handleSave = async (updatedDocument: Document) => {
        if (!onSaved) return;

        setIsLoading(true);
        try {
            // Notify parent component that save was successful
            await onSaved(updatedDocument.documentId);

            // Update the document in our local state
            setRemainingDocuments((prev) =>
                prev.map((r) =>
                    r.documentId === updatedDocument.documentId
                        ? updatedDocument
                        : r,
                ),
            );
        } catch (error) {
            console.error("Error saving document:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async (documentId: string) => {
        setIsLoading(true);
        try {
            // Remove the document from our local state
            const updatedDocuments = remainingDocuments.filter(
                (r) => r.documentId !== documentId,
            );

            // If there are no documents left after deletion, close the overlay
            if (updatedDocuments.length === 0) {
                onClose();
                return;
            }

            // Calculate the new index - stay at current index unless it's the last item
            const newIndex =
                currentIndex >= updatedDocuments.length
                    ? updatedDocuments.length - 1
                    : currentIndex;

            // Set animation direction based on where we're navigating
            if (newIndex < currentIndex) {
                // Going to previous item (when last item is deleted)
                setDirection("left");
            } else {
                // Staying at same index (which will show next item)
                setDirection("right");
            }

            // Update state and navigate
            setRemainingDocuments(updatedDocuments);
            setCurrentIndex(newIndex);

            // Notify parent component about the deletion
            if (onApproved) {
                await onApproved(documentId);
            }
        } catch (error) {
            console.error("Error handling document deletion:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Current document to display
    const currentDocument = remainingDocuments[currentIndex];

    // Count unapproved documents
    const unapprovedCount = remainingDocuments.filter(
        (r) => !r.isApproved,
    ).length;

    // Create dynamic title
    const dynamicTitle = `${title} - ${currentIndex + 1}/${remainingDocuments.length}${unapprovedCount > 0 ? ` (${unapprovedCount} unapproved)` : ""}`;

    if (!open || !currentDocument) {
        return null;
    }

    return (
        <FullScreenModal isOpen={open} onClose={onClose} title={dynamicTitle}>
            <AnimatePresence mode="wait" initial={false} custom={direction}>
                <motion.div
                    key={currentIndex}
                    custom={direction}
                    variants={{
                        enter: (direction) => ({
                            x: direction === "right" ? 500 : -500,
                            opacity: 0,
                        }),
                        center: {
                            x: 0,
                            opacity: 1,
                        },
                        exit: (direction) => ({
                            x: direction === "right" ? -500 : 500,
                            opacity: 0,
                        }),
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: {
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                            duration: 0.2,
                        },
                        opacity: { duration: 0.2 },
                    }}
                >
                    <DocumentEditor
                        document={currentDocument}
                        onSave={handleSave}
                        onApprove={handleApprove}
                        onRevokeApprove={handleRevokeApprove}
                        onDelete={handleDelete}
                        onNavigate={handleNavigate}
                        isLoading={isLoading}
                        hasNext={currentIndex < remainingDocuments.length - 1}
                        hasPrevious={currentIndex > 0}
                    />
                </motion.div>
            </AnimatePresence>
        </FullScreenModal>
    );
}
